import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Alert,
  Snackbar,
  LinearProgress,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tabs,
  Tab,
  Menu
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  PlayArrow as TestIcon,
  CloudDone as ConnectedIcon,
  CloudOff as DisconnectedIcon,
  MoreVert as MoreIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  ArrowBack as ArrowBackIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import * as apiIntegrationsService from '../../services/apiIntegrationsService';
import apiKeysService from '../../services/apiKeysService';
import APIIntegrationForm from './APIIntegrationForm';
import APIKeyForm from './APIKeyForm';
import ConnectionTestDialog from './ConnectionTestDialog';
import { useNavigate } from 'react-router-dom';

const APIIntegrations = () => {
  const [integrations, setIntegrations] = useState([]);
  const [filteredIntegrations, setFilteredIntegrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedIntegration, setSelectedIntegration] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [testDialogOpen, setTestDialogOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({ 
    open: false, 
    message: '', 
    severity: 'success'
  });
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [currentTab, setCurrentTab] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  
  // API Keys state
  const [apiKeys, setApiKeys] = useState([]);
  const [filteredApiKeys, setFilteredApiKeys] = useState([]);
  const [selectedApiKey, setSelectedApiKey] = useState(null);
  const [apiKeyFormOpen, setApiKeyFormOpen] = useState(false);
  const [apiKeySearchTerm, setApiKeySearchTerm] = useState('');
  const [apiKeyStatusFilter, setApiKeyStatusFilter] = useState('');
  const [showApiKeyDialog, setShowApiKeyDialog] = useState(false);

  const navigate = useNavigate();

  const filterApiKeys = useCallback(() => {
    let filtered = apiKeys;
    console.log('Filtering API keys:', { apiKeys, apiKeySearchTerm, apiKeyStatusFilter });

    if (apiKeySearchTerm) {
      filtered = filtered.filter(apiKey =>
        apiKey.name.toLowerCase().includes(apiKeySearchTerm.toLowerCase()) ||
        apiKey.description.toLowerCase().includes(apiKeySearchTerm.toLowerCase()) ||
        apiKey.organizationId.toLowerCase().includes(apiKeySearchTerm.toLowerCase())
      );
    }

    if (apiKeyStatusFilter) {
      filtered = filtered.filter(apiKey => 
        apiKeyStatusFilter === 'active' ? apiKey.isActive : !apiKey.isActive
      );
    }

    console.log('Filtered API keys:', filtered);
    setFilteredApiKeys(filtered);
  }, [apiKeys, apiKeySearchTerm, apiKeyStatusFilter]);

  const filterIntegrations = useCallback(() => {
    let filtered = integrations;

    if (searchTerm) {
      filtered = filtered.filter(integration =>
        integration.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        integration.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        integration.vendor?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (categoryFilter) {
      filtered = filtered.filter(integration => integration.category === categoryFilter);
    }

    if (statusFilter) {
      filtered = filtered.filter(integration => 
        statusFilter === 'active' ? integration.active : !integration.active
      );
    }

    setFilteredIntegrations(filtered);
  }, [integrations, searchTerm, categoryFilter, statusFilter]);

  useEffect(() => {
    loadIntegrations();
    loadApiKeys();
  }, []);

  useEffect(() => {
    if (currentTab === 1) { // Inbound tab (API Keys)
      filterApiKeys();
    } else {
      filterIntegrations();
    }
  }, [currentTab, filterApiKeys, filterIntegrations]);



  const loadApiKeys = async () => {
    try {
      setLoading(true);
      console.log('Loading API keys...');
      const data = await apiKeysService.getAllApiKeys();
      console.log('API keys loaded:', data);
      setApiKeys(data);
    } catch (error) {
      console.error('Error loading API keys:', error);
      setSnackbar({
        open: true,
        message: 'Failed to load API keys',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };



  const loadIntegrations = async () => {
    try {
      setLoading(true);
      const data = await apiIntegrationsService.getAllIntegrations();
      setIntegrations(data);
    } catch (error) {
      console.error('Error loading integrations:', error);
      setSnackbar({
        open: true,
        message: 'Failed to load integrations',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };



  const handleCreate = () => {
    if (currentTab === 1) { // Inbound tab (API Keys)
      setSelectedApiKey(null);
      setApiKeyFormOpen(true);
    } else {
      setSelectedIntegration(null);
      setFormOpen(true);
    }
  };

  const handleEdit = (item) => {
    if (currentTab === 1) { // Inbound tab (API Keys)
      setSelectedApiKey(item);
      setApiKeyFormOpen(true);
    } else {
      setSelectedIntegration(item);
      setFormOpen(true);
    }
    setMenuAnchor(null);
  };

  const handleDelete = (item) => {
    if (currentTab === 1) { // Inbound tab (API Keys)
      setSelectedApiKey(item);
    } else {
      setSelectedIntegration(item);
    }
    setDeleteConfirmOpen(true);
    setMenuAnchor(null);
  };

  const handleTest = (integration) => {
    setSelectedIntegration(integration);
    setTestDialogOpen(true);
    setMenuAnchor(null);
  };

  const confirmDelete = async () => {
    if (!selectedIntegration && !selectedApiKey) return;

    try {
      if (currentTab === 1) { // Inbound tab (API Keys)
        await apiKeysService.deleteApiKey(selectedApiKey.id);
        await loadApiKeys();
        setSnackbar({
          open: true,
          message: 'API key deleted successfully',
          severity: 'success'
        });
        setSelectedApiKey(null);
      } else {
        await apiIntegrationsService.deleteIntegration(selectedIntegration.id);
        await loadIntegrations();
        setSnackbar({
          open: true,
          message: 'Integration deleted successfully',
          severity: 'success'
        });
        setSelectedIntegration(null);
      }
    } catch (error) {
      console.error('Error deleting:', error);
      setSnackbar({
        open: true,
        message: `Failed to delete ${currentTab === 1 ? 'API key' : 'integration'}`,
        severity: 'error'
      });
    } finally {
      setDeleteConfirmOpen(false);
    }
  };

  const handleToggleStatus = async (item) => {
    try {
      if (currentTab === 1) { // Inbound tab (API Keys)
        await apiKeysService.toggleApiKeyStatus(item.id);
        await loadApiKeys();
        setSnackbar({
          open: true,
          message: `API key ${item.isActive ? 'deactivated' : 'activated'} successfully`,
          severity: 'success'
        });
      } else {
        await apiIntegrationsService.toggleIntegrationStatus(item.id);
        await loadIntegrations();
        setSnackbar({
          open: true,
          message: `Integration ${item.active ? 'deactivated' : 'activated'} successfully`,
          severity: 'success'
        });
      }
    } catch (error) {
      console.error('Error toggling status:', error);
      setSnackbar({
        open: true,
        message: `Failed to update ${currentTab === 1 ? 'API key' : 'integration'} status`,
        severity: 'error'
      });
    }
  };

  const handleFormSubmit = async (formData) => {
    try {
      if (currentTab === 1) { // Inbound tab (API Keys)
        if (selectedApiKey) {
          await apiKeysService.updateApiKey(selectedApiKey.id, formData);
          setSnackbar({
            open: true,
            message: 'API key updated successfully',
            severity: 'success'
          });
        } else {
          const newApiKey = await apiKeysService.createApiKey(formData);
          // Show the generated API key value to the user
          setSnackbar({
            open: true,
            message: 'API key created successfully! Please copy the key value.',
            severity: 'success'
          });
          // Store the newly created API key to display in a dialog
          setSelectedApiKey(newApiKey);
          setApiKeyFormOpen(false);
          // Show a dialog with the generated key
          setShowApiKeyDialog(true);
          return; // Don't reload API keys yet, wait for dialog close
        }
        await loadApiKeys();
        setApiKeyFormOpen(false);
        setSelectedApiKey(null);
      } else {
        if (selectedIntegration) {
          await apiIntegrationsService.updateIntegration(selectedIntegration.id, formData);
          setSnackbar({
            open: true,
            message: 'Integration updated successfully',
            severity: 'success'
          });
        } else {
          await apiIntegrationsService.createIntegration(formData);
          setSnackbar({
            open: true,
            message: 'Integration created successfully',
            severity: 'success'
          });
        }
        await loadIntegrations();
        setFormOpen(false);
        setSelectedIntegration(null);
      }
    } catch (error) {
      console.error('Error saving:', error);
      setSnackbar({
        open: true,
        message: `Failed to save ${currentTab === 1 ? 'API key' : 'integration'}`,
        severity: 'error'
      });
    }
  };

  const getStatusColor = (active) => {
    return active ? 'success' : 'default';
  };

  const getStatusIcon = (active) => {
    return active ? <ConnectedIcon /> : <DisconnectedIcon />;
  };

  const getCategoryColor = (category) => {
    const colors = {
      Healthcare: '#2196f3',
      Insurance: '#ff9800',
      Laboratory: '#4caf50',
      Pharmacy: '#9c27b0',
      Billing: '#f44336',
      EHR: '#00bcd4',
      Other: '#795548'
    };
    return colors[category] || '#9e9e9e';
  };

  const handleMenuClick = (event, integration) => {
    event.stopPropagation();
    setSelectedIntegration(integration);
    setMenuAnchor(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setSelectedIntegration(null);
  };

  const renderFilters = () => (
    <Box sx={{ mb: 3, p: 3, backgroundColor: 'white', borderRadius: '8px', boxShadow: '0 4px 16px rgba(0,0,0,0.1)' }}>
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={12} sm={4}>
          <TextField
            fullWidth
            placeholder="Search integrations..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
            }}
            size="small"
          />
        </Grid>
        <Grid item xs={12} sm={3}>
          <FormControl fullWidth size="small">
            <InputLabel>Category</InputLabel>
            <Select
              value={categoryFilter}
              label="Category"
              onChange={(e) => setCategoryFilter(e.target.value)}
            >
              <MenuItem value="">All Categories</MenuItem>
              {apiIntegrationsService.getIntegrationCategories().map(category => (
                <MenuItem key={category} value={category}>{category}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={3}>
          <FormControl fullWidth size="small">
            <InputLabel>Status</InputLabel>
            <Select
              value={statusFilter}
              label="Status"
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <MenuItem value="">All Status</MenuItem>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="inactive">Inactive</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={2}>
          <Button
            fullWidth
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={loadIntegrations}
          >
            Refresh
          </Button>
        </Grid>
      </Grid>
    </Box>
  );

  const renderApiKeyFilters = () => (
    <Box sx={{ mb: 3, p: 3, backgroundColor: 'white', borderRadius: '8px', boxShadow: '0 4px 16px rgba(0,0,0,0.1)' }}>
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            placeholder="Search API keys by name, description, or organization..."
            value={apiKeySearchTerm}
            onChange={(e) => setApiKeySearchTerm(e.target.value)}
            InputProps={{
              startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
            }}
            size="small"
          />
        </Grid>
        <Grid item xs={12} sm={3}>
          <FormControl fullWidth size="small">
            <InputLabel>Status</InputLabel>
            <Select
              value={apiKeyStatusFilter}
              label="Status"
              onChange={(e) => setApiKeyStatusFilter(e.target.value)}
            >
              <MenuItem value="">All Status</MenuItem>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="inactive">Inactive</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={3}>
          <Button
            fullWidth
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={loadApiKeys}
          >
            Refresh
          </Button>
        </Grid>
      </Grid>
    </Box>
  );

  const renderIntegrationsTable = () => (
    <TableContainer component={Paper} sx={{ borderRadius: '8px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Name & Description</TableCell>
            <TableCell align="center">Type</TableCell>
            <TableCell align="center">Category</TableCell>
            <TableCell align="center">Status</TableCell>
            <TableCell align="center">Environment</TableCell>
            <TableCell align="center">Last Updated</TableCell>
            <TableCell align="center">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {filteredIntegrations.map((integration) => (
            <TableRow key={integration.id} hover>
              <TableCell>
                <Box>
                  <Typography variant="subtitle2" fontWeight="bold">
                    {integration.name}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {integration.description}
                  </Typography>
                  {integration.vendor && (
                    <Typography variant="caption" color="textSecondary">
                      Vendor: {integration.vendor}
                    </Typography>
                  )}
                </Box>
              </TableCell>
              <TableCell align="center">
                <Chip
                  label={integration.type}
                  size="small"
                  variant="outlined"
                />
              </TableCell>
              <TableCell align="center">
                <Chip
                  label={integration.category}
                  size="small"
                  sx={{
                    backgroundColor: getCategoryColor(integration.category),
                    color: 'white'
                  }}
                />
              </TableCell>
              <TableCell align="center">
                <Chip
                  icon={getStatusIcon(integration.active)}
                  label={integration.active ? 'Active' : 'Inactive'}
                  color={getStatusColor(integration.active)}
                  size="small"
                />
              </TableCell>
              <TableCell align="center">
                <Chip
                  label={integration.environment}
                  size="small"
                  color={integration.environment === 'production' ? 'error' : 
                         integration.environment === 'staging' ? 'warning' : 'info'}
                  variant="outlined"
                />
              </TableCell>
              <TableCell align="center">
                <Typography variant="body2">
                  {integration.updatedAt && (() => {
                    try {
                      const date = new Date(integration.updatedAt);
                      if (isNaN(date.getTime())) {
                        return 'Invalid date';
                      }
                      return format(date, 'MMM dd, yyyy');
                    } catch (error) {
                      return 'Invalid date';
                    }
                  })()}
                </Typography>
              </TableCell>
              <TableCell align="center">
                <Box display="flex" gap={1} justifyContent="center">
                  <IconButton
                    size="small"
                    onClick={() => handleTest(integration)}
                    color="primary"
                    title="Test Connection"
                  >
                    <TestIcon />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={(e) => handleMenuClick(e, integration)}
                  >
                    <MoreIcon />
                  </IconButton>
                </Box>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {filteredIntegrations.length === 0 && (
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="body1" color="textSecondary">
            No integrations found. {searchTerm || categoryFilter || statusFilter ? 'Try adjusting your filters.' : 'Get started by adding your first integration.'}
          </Typography>
        </Box>
      )}
    </TableContainer>
  );

  const TabPanel = ({ children, value, index, ...other }) => (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`api-integrations-tabpanel-${index}`}
      aria-labelledby={`api-integrations-tab-${index}`}
      {...other}
    >
      {value === index && <Box>{children}</Box>}
    </div>
  );

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ 
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', 
        borderRadius: '16px', 
        mb: 3,
        boxShadow: '0 8px 32px rgba(102, 126, 234, 0.3)',
        py: 1.5,
        px: 3
      }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <IconButton onClick={() => navigate(-1)} sx={{ color: 'white', mr: 2 }}>
              <ArrowBackIcon />
            </IconButton>
            <Box>
              <Typography variant="h4" sx={{ color: 'white', fontWeight: 700, mb: 0.5 }}>
                API Integrations
              </Typography>
              <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                Configure and manage system integrations
              </Typography>
            </Box>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreate}
            size="large"
            sx={{ bgcolor: 'rgba(255,255,255,0.2)', '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' }, borderRadius: '8px', textTransform: 'none', fontWeight: 600 }}
          >
            {currentTab === 1 ? 'Add API Key' : 'Add Integration'}
          </Button>
        </Box>
      </Box>

      {loading && <LinearProgress sx={{ mb: 2 }} />}

      <Tabs
        value={currentTab}
        onChange={(_, newValue) => setCurrentTab(newValue)}
        sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
      >
        <Tab label="Outbound" />
        <Tab label="Inbound" />
      </Tabs>

      <TabPanel value={currentTab} index={0}>
        {renderFilters()}
        {renderIntegrationsTable()}
      </TabPanel>

      <TabPanel value={currentTab} index={1}>
        {renderApiKeyFilters()}
        <Box sx={{ p: 3, backgroundColor: 'white', borderRadius: '8px', boxShadow: '0 4px 16px rgba(0,0,0,0.1)' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h6">API Keys Management</Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => {
                setSelectedApiKey(null);
                setApiKeyFormOpen(true);
              }}
              size="small"
            >
              Add API Key
            </Button>
          </Box>

          <TableContainer component={Paper} sx={{ borderRadius: '8px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name & Description</TableCell>
                  <TableCell align="center">Organization</TableCell>
                  <TableCell align="center">Scopes</TableCell>
                  <TableCell align="center">Environment</TableCell>
                  <TableCell align="center">Status</TableCell>
                  <TableCell align="center">Last Used</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredApiKeys.map((apiKey) => (
                  <TableRow key={apiKey.id} hover>
                    <TableCell>
                      <Box>
                        <Typography variant="subtitle2" fontWeight="bold">
                          {apiKey.name}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          {apiKey.description}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body2">
                        {apiKey.organizationId}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Box display="flex" flexWrap="wrap" gap={0.5} justifyContent="center">
                        {apiKey.scopes && apiKey.scopes.map((scope) => (
                          <Chip
                            key={scope}
                            label={scope}
                            size="small"
                            variant="outlined"
                          />
                        ))}
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={apiKey.environment}
                        size="small"
                        color={apiKey.environment === 'production' ? 'error' : 
                               apiKey.environment === 'staging' ? 'warning' : 'info'}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        icon={getStatusIcon(apiKey.isActive)}
                        label={apiKey.isActive ? 'Active' : 'Inactive'}
                        color={getStatusColor(apiKey.isActive)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body2">
                        {apiKey.lastUsed ? format(new Date(apiKey.lastUsed), 'MMM dd, yyyy HH:mm') : 'Never'}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Box display="flex" gap={1} justifyContent="center">
                        <IconButton
                          size="small"
                          onClick={() => handleToggleStatus(apiKey)}
                          color={apiKey.isActive ? 'error' : 'success'}
                          title={apiKey.isActive ? 'Deactivate' : 'Activate'}
                        >
                          {apiKey.isActive ? <DisconnectedIcon /> : <ConnectedIcon />}
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleEdit(apiKey)}
                          color="primary"
                          title="Edit"
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleDelete(apiKey)}
                          color="error"
                          title="Delete"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {filteredApiKeys.length === 0 && (
              <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography variant="body1" color="textSecondary">
                  No API keys found. {apiKeySearchTerm ? 'Try adjusting your search.' : 'Get started by adding your first API key.'}
                </Typography>
              </Box>
            )}
          </TableContainer>
        </Box>
      </TabPanel>

      {/* Context Menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => handleEdit(selectedIntegration)}>
          <EditIcon sx={{ mr: 1 }} />
          Edit
        </MenuItem>
        <MenuItem onClick={() => handleTest(selectedIntegration)}>
          <TestIcon sx={{ mr: 1 }} />
          Test Connection
        </MenuItem>
        <MenuItem onClick={() => handleDelete(selectedIntegration)}>
          <DeleteIcon sx={{ mr: 1 }} />
          Delete
        </MenuItem>
      </Menu>

      {/* Form Dialogs */}
      <APIIntegrationForm
        open={formOpen}
        integration={selectedIntegration}
        onClose={() => {
          setFormOpen(false);
          setSelectedIntegration(null);
        }}
        onSubmit={handleFormSubmit}
      />

      <APIKeyForm
        open={apiKeyFormOpen}
        apiKey={selectedApiKey}
        onClose={() => {
          setApiKeyFormOpen(false);
          setSelectedApiKey(null);
        }}
        onSubmit={handleFormSubmit}
      />

      {/* Test Dialog */}
      <ConnectionTestDialog
        open={testDialogOpen}
        integration={selectedIntegration}
        onClose={() => {
          setTestDialogOpen(false);
          setSelectedIntegration(null);
        }}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the {currentTab === 1 ? 'API key' : 'integration'} "{currentTab === 1 ? selectedApiKey?.name : selectedIntegration?.name}"?
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
          <Button onClick={confirmDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* API Key Display Dialog */}
      <Dialog 
        open={showApiKeyDialog} 
        onClose={() => {
          setShowApiKeyDialog(false);
          setSelectedApiKey(null);
          loadApiKeys(); // Reload API keys after dialog closes
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>API Key Created Successfully</DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 2 }}>
            <Typography variant="body1" sx={{ mb: 2 }}>
              Your API key has been created successfully. Please copy the key value below and store it securely. 
              <strong>This is the only time you will see the full key value.</strong>
            </Typography>
            
            <Typography variant="h6" sx={{ mb: 1 }}>
              API Key Details:
            </Typography>
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="textSecondary">
                <strong>Name:</strong> {selectedApiKey?.name}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                <strong>Organization ID:</strong> {selectedApiKey?.organizationId}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                <strong>Scopes:</strong> {selectedApiKey?.scopes?.join(', ')}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                <strong>Environment:</strong> {selectedApiKey?.environment}
              </Typography>
            </Box>

            <Typography variant="h6" sx={{ mb: 1 }}>
              API Key Value:
            </Typography>
            <Box 
              sx={{ 
                p: 2, 
                backgroundColor: '#f5f5f5', 
                borderRadius: 1, 
                border: '1px solid #ddd',
                fontFamily: 'monospace',
                fontSize: '14px',
                wordBreak: 'break-all',
                position: 'relative'
              }}
            >
              {selectedApiKey?.keyValue}
              <Button
                size="small"
                variant="outlined"
                sx={{ 
                  position: 'absolute', 
                  top: 8, 
                  right: 8,
                  minWidth: 'auto',
                  px: 1
                }}
                onClick={() => {
                  navigator.clipboard.writeText(selectedApiKey?.keyValue || '');
                  setSnackbar({
                    open: true,
                    message: 'API key copied to clipboard!',
                    severity: 'success'
                  });
                }}
              >
                Copy
              </Button>
            </Box>
            
            <Alert severity="warning" sx={{ mt: 2 }}>
              <Typography variant="body2">
                <strong>Important:</strong> Store this API key securely. You won't be able to see the full key value again.
                Third parties will need this key to authenticate with your API.
              </Typography>
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => {
              setShowApiKeyDialog(false);
              setSelectedApiKey(null);
              loadApiKeys();
            }}
            variant="contained"
            color="primary"
          >
            Done
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default APIIntegrations;
